import React, { forwardRef, useCallback, useMemo, useRef, useState } from 'react';
import { Icon } from '../primitives/Icon';
import { Platform, Pressable } from 'react-native';
import DateTimePicker from '@react-native-community/datetimepicker';
import { XStack, YStack } from 'tamagui';
import { Text } from '../primitives/Text';
import { useThemeColors } from '../hooks/useThemeColors';
import { Temporal } from '@js-temporal/polyfill';
import { MaskedInput } from './MaskedInput';
import type { MaskedInputProps } from './MaskedInput';
import type { TextInput } from 'react-native';
import { dateFormatToMask, displayToIso, isoToDisplay } from '../utils/dateFormat';

/**
 * Parse an ISO date for the native picker. Returns null on failure so
 * the caller can decide a safe default — silently falling back to
 * `new Date()` (today) means a confirming tap on the picker overwrites
 * an invalid/mid-edit value with today, which the user never asked for.
 */
const isoToDateLocal = (iso: string): Date | null => {
  try {
    const plain = Temporal.PlainDate.from(iso);
    return new Date(plain.year, plain.month - 1, plain.day);
  } catch {
    return null;
  }
};

const dateToIsoLocal = (d: Date): string => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const DEFAULT_DATE_FORMAT = 'YYYY-MM-DD';

/** Value is always ISO "YYYY-MM-DD" — the format string only affects display. */
export type DateInputProps = Omit<
  MaskedInputProps,
  'type' | 'options' | 'mask' | 'value' | 'onChangeText'
> & {
  /** Current date in ISO "YYYY-MM-DD" (storage format, not display). */
  value: string;
  /** Called with ISO "YYYY-MM-DD". */
  onChangeText: (value: string) => void;
  /**
   * Display format — what the user sees and types. Defaults to ISO.
   * Recognised tokens: YYYY, MM, DD. Examples: "YYYY-MM-DD",
   * "DD/MM/YYYY", "MM-DD-YYYY". Internal/emitted value stays ISO.
   * Per ADR-0076 — previously ignored (DateInput.tsx:76 bug).
   */
  dateFormat?: string | undefined;
  /** Show calendar picker button (default true) — native only. */
  showCalendar?: boolean | undefined;
  /** Show a "Use <format>" caption on invalid values. Default false. */
  showFormatHint?: boolean | undefined;
  /** Minimum selectable date for the picker. */
  minimumDate?: Date | undefined;
  /** Maximum selectable date for the picker. */
  maximumDate?: Date | undefined;
  /**
   * BCP-47 locale for the calendar picker display. Defaults to the
   * host's resolved locale. Doesn't affect the format string — pass
   * `dateFormat` to change the typed format.
   */
  locale?: string | undefined;
  /** Variant forwarded to the inner MaskedInput. Auto-picks from row context. */
  variant?: MaskedInputProps['variant'] | undefined;
};

/**
 * Date input with visible mask and calendar picker.
 *
 * Per ADR-0076, `dateFormat` is honored — the mask is derived from the
 * format string, the user sees and types in that format, but the
 * prop value + onChangeText always speak ISO so storage code stays
 * unchanged.
 */
export const DateInput = forwardRef<TextInput | null, DateInputProps>(
  (
    {
      value,
      onChangeText,
      dateFormat = DEFAULT_DATE_FORMAT,
      placeholder,
      showCalendar = true,
      showFormatHint = false,
      minimumDate,
      maximumDate,
      locale,
      variant,
      ...rest
    },
    ref
  ) => {
    const theme = useThemeColors();
    const [showPicker, setShowPicker] = useState(false);
    // Snapshot the seed date the picker is initialised with, so we can
    // detect a no-op "OK" tap (Android fires onChange with the seed
    // value when the user confirms without spinning the wheel). When
    // the original ISO was invalid we don't want today silently
    // overwriting the user's mid-edit value.
    const pickerSeedRef = useRef<string | null>(null);
    const pickerSeededFromInvalidRef = useRef(false);
    const inputRef = useRef<TextInput | null>(null);

    const mask = useMemo(() => dateFormatToMask(dateFormat), [dateFormat]);
    const resolvedPlaceholder = placeholder ?? dateFormat;

    // Display format ↔ ISO round-trip. The masked input shows the
    // current display string; we translate to ISO before bubbling out.
    const displayValue = useMemo(
      () => (value ? isoToDisplay(value, dateFormat) : ''),
      [value, dateFormat],
    );

    const handleMaskedChange = useCallback(
      (next: string) => {
        if (!next) {
          onChangeText('');
          return;
        }
        onChangeText(displayToIso(next, dateFormat));
      },
      [onChangeText, dateFormat],
    );

    const handlePickerChange = (_: unknown, d: Date | undefined) => {
      if (Platform.OS === 'android') setShowPicker(false);
      if (!d) return;
      const next = dateToIsoLocal(d);
      // Suppress the no-op "OK" overwrite: if the picker was seeded
      // from an invalid value (today fallback) and the user confirmed
      // without changing anything, don't fire onChangeText.
      if (pickerSeededFromInvalidRef.current && next === pickerSeedRef.current) {
        pickerSeededFromInvalidRef.current = false;
        pickerSeedRef.current = null;
        return;
      }
      pickerSeededFromInvalidRef.current = false;
      pickerSeedRef.current = null;
      onChangeText(next);
    };

    const openPicker = useCallback(() => {
      const parsed = isoToDateLocal(value);
      const seed = parsed ?? new Date();
      pickerSeedRef.current = dateToIsoLocal(seed);
      pickerSeededFromInvalidRef.current = parsed === null;
      setShowPicker(true);
    }, [value]);

    const showNativeCalendarButton = showCalendar && Platform.OS !== 'web';
    const isValid = !value || /^\d{4}-\d{2}-\d{2}$/.test(value);

    return (
      <YStack gap="$1" width="100%" minWidth={0}>
        {showFormatHint && !isValid ? (
          <Text variant="caption" color="$textError" marginLeft={2}>
            Use {dateFormat} (e.g. {isoToDisplay('2025-06-01', dateFormat)})
          </Text>
        ) : null}
        <XStack alignItems="center" gap="$2">
          <MaskedInput
            ref={(r) => {
              (inputRef).current = r;
              if (typeof ref === 'function') ref(r);
              else if (ref) (ref).current = r;
            }}
            type="date"
            mask={mask}
            value={displayValue}
            onChangeText={handleMaskedChange}
            placeholder={resolvedPlaceholder}
            variant={variant}
            style={{ flex: 1 }}
            {...rest}
          />
          {showNativeCalendarButton ? (
            <>
              <Pressable
                onPress={openPicker}
                accessibilityLabel="Open calendar"
                accessibilityRole="button"
                hitSlop={8}
                style={({ pressed }) => [
                  { padding: 4, opacity: pressed ? 0.6 : 1 },
                ]}
              >
                <Icon
                  name="calendar-today"
                  size={20}
                  color={theme.textSubdued}
                />
              </Pressable>
              {showPicker ? (
                <DateTimePicker
                  // Seed comes from `openPicker` which also records
                  // whether the parse failed — `handlePickerChange`
                  // then suppresses a no-op "OK" overwrite when the
                  // user confirms without spinning the wheel.
                  value={isoToDateLocal(value) ?? new Date()}
                  mode="date"
                  display={Platform.OS === 'ios' ? 'spinner' : 'default'}
                  onChange={handlePickerChange}
                  {...(locale ? { locale } : {})}
                  {...(minimumDate ? { minimumDate } : {})}
                  {...(maximumDate ? { maximumDate } : {})}
                />
              ) : null}
            </>
          ) : null}
        </XStack>
      </YStack>
    );
  }
);

DateInput.displayName = 'DateInput';
